import { OpcaoRenda } from '../../enum/parametros/opcao-renda.enum';
import { VariavelValorMinimo } from '../../enum/parametros/variavel-valor-minimo.enum';
import {
  BeneficioResponseDto,
  BeneficioResumoDto,
  ComprovacaoElegibilidadeResponseDto,
  ElegibilidadeBeneficioResponseDto,
  PlanoAntecipacaoResponseDto,
  PlanoOpcoesRendaResponseDto,
  PlanoRentabilidadeResponseDto,
  PlanoValorMinimoSaldoResponseDto,
  ResumoCatalogoDto,
  SalvarElegibilidadeBeneficioCommandDto,
  SalvarPlanoAntecipacaoCommandDto,
  SalvarPlanoOpcoesRendaCommandDto,
  SalvarPlanoRentabilidadeCommandDto,
  SalvarPlanoValorMinimoSaldoCommandDto,
  TipoRegraValorMinimoSaldoDto,
  TipoValorMinimoRendaDto,
  UnidadeTempoServicoDto,
  VariavelValorMinimoSaldoDto,
} from '../../models/parametros/beneficios-api.model';
import { SituacaoCadastralResponseDto } from '../../models/parametros/extrato-desligamento-api.model';
import {
  Antecipacao,
  BeneficioElegibilidade,
  BeneficioOpcao,
  ElegibilidadeBeneficios,
  OpcaoComprovacao,
  OpcoesRenda,
  RegraImpedimento,
  RegraValorMinimoSaldo,
  Rentabilidade,
  TipoValorMinimoRenda,
  UnidadeTempo,
} from '../../models/parametros/beneficios.model';
import { OpcaoSituacao } from '../../models/parametros/extrato-desligamento.model';
import {
  OPCOES_RENDA_OPCOES,
  percentualRendaMinimaOk,
  percentualSaldoOk,
  prazoCertoOk,
  valorRendaMinimaOk,
} from '../../utils/parametros/beneficios.defaults';
import { situacaoDtoToOpcao } from './extrato-desligamento-api.mapper';

const UNIDADE_DA_API: Readonly<Record<UnidadeTempoServicoDto, UnidadeTempo>> = { A: 'anos', M: 'meses' };
const UNIDADE_PARA_API: Readonly<Record<UnidadeTempo, UnidadeTempoServicoDto>> = { anos: 'A', meses: 'M' };
const TIPO_MINIMO_DA_API: Readonly<Record<TipoValorMinimoRendaDto, TipoValorMinimoRenda>> = { V: 'valor', P: 'percentual' };
const TIPO_MINIMO_PARA_API: Readonly<Record<TipoValorMinimoRenda, TipoValorMinimoRendaDto>> = { valor: 'V', percentual: 'P' };
const TIPO_REGRA_DA_API: Readonly<Record<TipoRegraValorMinimoSaldoDto, RegraValorMinimoSaldo['tipo']>> = { F: 'fixo', V: 'variavel' };
const TIPO_REGRA_PARA_API: Readonly<Record<RegraValorMinimoSaldo['tipo'], TipoRegraValorMinimoSaldoDto>> = { fixo: 'F', variavel: 'V' };
const VARIAVEL_DA_API: Readonly<Record<VariavelValorMinimoSaldoDto, VariavelValorMinimo>> = {
  SALARIO: VariavelValorMinimo.Salario,
  SALARIO_MINIMO: VariavelValorMinimo.SalarioMinimo,
  SALDO: VariavelValorMinimo.Saldo,
  UNIDADE_PREVIDENCIARIA: VariavelValorMinimo.UnidadePrevidenciaria,
};
const VARIAVEL_PARA_API: Readonly<Record<VariavelValorMinimo, VariavelValorMinimoSaldoDto>> = {
  [VariavelValorMinimo.Salario]: 'SALARIO',
  [VariavelValorMinimo.SalarioMinimo]: 'SALARIO_MINIMO',
  [VariavelValorMinimo.Saldo]: 'SALDO',
  [VariavelValorMinimo.UnidadePrevidenciaria]: 'UNIDADE_PREVIDENCIARIA',
};

const positivoComDuasCasas = (valor: number | null): valor is number =>
  valor !== null && valor > 0 && /^\d+(\.\d{1,2})?$/.test(String(valor));

export function antecipacaoDtoToAntecipacao(dto: PlanoAntecipacaoResponseDto): Antecipacao {
  return {
    saldo: {
      permitir: dto.fl_permite_antecip_sld,
      percentualMinimo: dto.pc_min_antecip_sld ?? null,
      percentualMaximo: dto.pc_max_antecip_sld ?? null,
    },
    adiantamentoBeneficio: {
      elegivel: dto.fl_elegivel_adiant,
      percentual: dto.pc_adiant_benef ?? null,
    },
  };
}

export function antecipacaoToSalvarCommand({ saldo, adiantamentoBeneficio }: Antecipacao): SalvarPlanoAntecipacaoCommandDto {
  return {
    fl_permite_antecip_sld: saldo.permitir,
    pc_min_antecip_sld: saldo.percentualMinimo,
    pc_max_antecip_sld: saldo.percentualMaximo,
    fl_elegivel_adiant: adiantamentoBeneficio.elegivel,
    pc_adiant_benef: adiantamentoBeneficio.percentual,
  };
}

/**
 * Opções de renda na tela, na ordem da tela e com as chaves na ordem do formulário (a tela compara por
 * JSON para saber se há alterações). Sem unidade do prazo ou tipo do mínimo (a API sempre os manda),
 * ficam anos e valor, como no padrão da tela.
 */
export function opcoesRendaDtoToOpcoesRenda(dto: PlanoOpcoesRendaResponseDto): OpcoesRenda {
  const marcadas: Readonly<Record<OpcaoRenda, boolean>> = {
    [OpcaoRenda.ValorFixoMensal]: dto.fl_vlr_fixo_mensal,
    [OpcaoRenda.PercentualSaldo]: dto.fl_pct_saldo,
    [OpcaoRenda.PrazoCerto]: dto.fl_prazo_certo,
    [OpcaoRenda.PagamentoUnico]: dto.fl_pagto_unico,
  };
  return {
    opcoes: OPCOES_RENDA_OPCOES.map(({ value }) => value).filter((opcao) => marcadas[opcao]),
    parametros: {
      percentualSaldo: { minimo: dto.pct_saldo_min ?? null, maximo: dto.pct_saldo_max ?? null },
      prazoCerto: {
        minimo: dto.qtd_prazo_min ?? null,
        maximo: dto.qtd_prazo_max ?? null,
        unidade: UNIDADE_DA_API[dto.tp_unid_prazo] ?? 'anos',
      },
    },
    valorMinimo: {
      validar: dto.fl_valida_vlr_min,
      tipo: TIPO_MINIMO_DA_API[dto.tp_vlr_min] ?? 'valor',
      valor: dto.vlr_min_renda ?? null,
      percentual: dto.pct_min_renda ?? null,
    },
  };
}

/**
 * Body do Salvar das opções de renda. Os valores de opção desmarcada, do tipo de mínimo fora de uso e do
 * mínimo com a validação desligada também vão (ficam guardados), mas só os que a API aceita: um valor
 * inválido vai nulo, em vez de barrar o Salvar por um campo que não aparece na tela.
 */
export function opcoesRendaToSalvarCommand({ opcoes, parametros, valorMinimo }: OpcoesRenda): SalvarPlanoOpcoesRendaCommandDto {
  const { percentualSaldo, prazoCerto } = parametros;
  const faixaPercentual = faixaAceita(percentualSaldo, percentualSaldoOk);
  const faixaPrazo = faixaAceita(prazoCerto, (valor) => prazoCertoOk(valor, prazoCerto.unidade));
  return {
    fl_vlr_fixo_mensal: opcoes.includes(OpcaoRenda.ValorFixoMensal),
    fl_pct_saldo: opcoes.includes(OpcaoRenda.PercentualSaldo),
    pct_saldo_min: faixaPercentual.minimo,
    pct_saldo_max: faixaPercentual.maximo,
    fl_prazo_certo: opcoes.includes(OpcaoRenda.PrazoCerto),
    qtd_prazo_min: faixaPrazo.minimo,
    qtd_prazo_max: faixaPrazo.maximo,
    tp_unid_prazo: UNIDADE_PARA_API[prazoCerto.unidade],
    fl_pagto_unico: opcoes.includes(OpcaoRenda.PagamentoUnico),
    fl_valida_vlr_min: valorMinimo.validar,
    tp_vlr_min: TIPO_MINIMO_PARA_API[valorMinimo.tipo],
    vlr_min_renda: valorRendaMinimaOk(valorMinimo.valor) ? valorMinimo.valor : null,
    pct_min_renda: percentualRendaMinimaOk(valorMinimo.percentual) ? valorMinimo.percentual : null,
  };
}

export function rentabilidadeDtoToRentabilidade(dto: PlanoRentabilidadeResponseDto): Rentabilidade {
  return {
    percentualMinimo: dto.pc_min_rentab ?? null,
    percentualMaximo: dto.pc_max_rentab ?? null,
    percentualPadrao: dto.pc_padrao_rentab ?? null,
  };
}

export function rentabilidadeToSalvarCommand({
  percentualMinimo,
  percentualMaximo,
  percentualPadrao,
}: Rentabilidade): SalvarPlanoRentabilidadeCommandDto {
  return {
    pc_min_rentab: percentualMinimo,
    pc_max_rentab: percentualMaximo,
    pc_padrao_rentab: percentualPadrao,
  };
}

/** Chaves na ordem do formulário: a tela compara por JSON para saber se há alterações. */
export function regraValorMinimoSaldoDtoToRegra(dto: PlanoValorMinimoSaldoResponseDto): RegraValorMinimoSaldo {
  return {
    habilitada: dto.fl_regra_habilitada,
    tipo: TIPO_REGRA_DA_API[dto.tp_regra] ?? 'fixo',
    valor: dto.vlr_min_fixo ?? null,
    variavel: dto.tp_variavel ? (VARIAVEL_DA_API[dto.tp_variavel] ?? null) : null,
    multiplicador: dto.vlr_multiplicador ?? null,
  };
}

/**
 * Body do Salvar da regra. Os valores da forma fora de uso e da regra desligada também vão (ficam
 * guardados), mas só os que a API aceita: um valor inválido vai nulo, em vez de barrar o Salvar por um
 * campo que não aparece na tela.
 */
export function regraValorMinimoSaldoToSalvarCommand({
  habilitada,
  tipo,
  valor,
  variavel,
  multiplicador,
}: RegraValorMinimoSaldo): SalvarPlanoValorMinimoSaldoCommandDto {
  return {
    fl_regra_habilitada: habilitada,
    tp_regra: TIPO_REGRA_PARA_API[tipo],
    vlr_min_fixo: positivoComDuasCasas(valor) ? valor : null,
    tp_variavel: variavel ? (VARIAVEL_PARA_API[variavel] ?? null) : null,
    vlr_multiplicador: positivoComDuasCasas(multiplicador) ? multiplicador : null,
  };
}

/** A faixa só com o que a API aceita: valor fora da regra vai nulo; com o mínimo acima do máximo, os dois. */
function faixaAceita(
  { minimo, maximo }: { minimo: number | null; maximo: number | null },
  ok: (valor: number | null) => boolean,
): { minimo: number | null; maximo: number | null } {
  const min = ok(minimo) ? minimo : null;
  const max = ok(maximo) ? maximo : null;
  return min !== null && max !== null && min > max ? { minimo: null, maximo: null } : { minimo: min, maximo: max };
}

/** Nome de exibição do catálogo; sem ele, o código. */
function nomeExibicao(dto: Pick<BeneficioResumoDto, 'nome' | 'nome_exibicao'>): string {
  return (dto.nome_exibicao || dto.nome).trim();
}

export function beneficioDtoToOpcao(dto: BeneficioResponseDto | BeneficioResumoDto): BeneficioOpcao {
  return { id: dto.id, nome: nomeExibicao(dto) };
}

export function comprovacaoDtoToOpcao(dto: ComprovacaoElegibilidadeResponseDto | ResumoCatalogoDto): OpcaoComprovacao {
  return { value: dto.id, label: nomeExibicao(dto) };
}

/**
 * Benefício na tela. Benefício que ainda não está no plano (`fl_ativo` nulo) entra ativo; sem tempo de
 * serviço, a unidade fica em anos (a API devolve 'A' por padrão).
 */
export function elegibilidadeDtoToBeneficio(dto: ElegibilidadeBeneficioResponseDto): BeneficioElegibilidade {
  const tempoMinimoServico = dto.qtd_tempo_min_servico ?? null;
  return {
    id: dto.id_beneficio,
    nome: dto.beneficio ? nomeExibicao(dto.beneficio) : String(dto.id_beneficio),
    ativo: dto.fl_ativo ?? true,
    regras: {
      desligamentoObrigatorio: dto.fl_deslig_obrigatorio,
      situacoesElegiveis: (dto.situacoes_cadastrais ?? []).map((situacao) => situacao.id),
      tempoMinimoServico,
      unidadeTempoServico:
        tempoMinimoServico !== null && dto.tp_unidade_tempo_serv ? UNIDADE_DA_API[dto.tp_unidade_tempo_serv] : 'anos',
      combinacoes: (dto.combinacoes ?? []).map((combinacao) => ({
        idadeMinima: combinacao.qtd_idade_minima ?? null,
        tempoMinimoVinculacao: combinacao.qtd_tempo_vinculacao ?? null,
      })),
      comprovacoes: (dto.comprovacoes ?? []).map((comprovacao) => comprovacao.id),
      beneficiarios: {
        quantidade: dto.qtd_max_beneficiario ?? null,
        percentualMinimo: dto.vlr_perc_min_beneficiario ?? null,
      },
      impedimentos: (dto.impedimentos ?? []).map(
        (impedimento): RegraImpedimento => ({
          id: String(impedimento.id),
          idApi: impedimento.id,
          beneficios: (impedimento.beneficios ?? []).map((beneficio) => beneficio.id),
          idadeMinima: impedimento.qtd_idade_minima ?? null,
          idadeMaxima: impedimento.qtd_idade_maxima ?? null,
          mensagem: impedimento.desc_msg_nao_elegivel ?? '',
        }),
      ),
      observacoes: dto.desc_observacao ?? '',
    },
    excecoes: dto.desc_excecao ?? '',
  };
}

/**
 * Monta o que a tela carrega. Os catálogos vêm só com os registros ativos; uma situação ou comprovação
 * já gravada que saiu do catálogo entra no fim da lista, para continuar com nome na tela.
 */
export function elegibilidadesDtoToElegibilidadeBeneficios(
  dtos: readonly ElegibilidadeBeneficioResponseDto[],
  situacoesCatalogo: readonly SituacaoCadastralResponseDto[],
  comprovacoesCatalogo: readonly ComprovacaoElegibilidadeResponseDto[],
): ElegibilidadeBeneficios {
  const situacoes = comCatalogoGravado(
    situacoesCatalogo.map(situacaoDtoToOpcao),
    dtos.flatMap((dto) => dto.situacoes_cadastrais ?? []).map(situacaoDtoToOpcao),
  );
  const comprovacoes = comCatalogoGravado(
    comprovacoesCatalogo.map(comprovacaoDtoToOpcao),
    dtos.flatMap((dto) => dto.comprovacoes ?? []).map(comprovacaoDtoToOpcao),
  );
  return { beneficios: dtos.map(elegibilidadeDtoToBeneficio), situacoes, comprovacoes };
}

function comCatalogoGravado<T extends OpcaoSituacao | OpcaoComprovacao>(catalogo: T[], gravadas: readonly T[]): T[] {
  for (const opcao of gravadas) {
    if (!catalogo.some((item) => item.value === opcao.value)) {
      catalogo.push(opcao);
    }
  }
  return catalogo;
}

/**
 * Body do Salvar de um benefício. Textos vazios vão nulos; sem tempo de serviço, a unidade também vai
 * nula. Impedimentos sem `idApi` são criados; os que saíram da tela são excluídos pela API.
 */
export function beneficioToSalvarCommand({ ativo, regras, excecoes }: BeneficioElegibilidade): SalvarElegibilidadeBeneficioCommandDto {
  return {
    fl_ativo: ativo,
    fl_deslig_obrigatorio: regras.desligamentoObrigatorio,
    qtd_tempo_min_servico: regras.tempoMinimoServico,
    tp_unidade_tempo_serv: regras.tempoMinimoServico !== null ? UNIDADE_PARA_API[regras.unidadeTempoServico] : null,
    qtd_max_beneficiario: regras.beneficiarios.quantidade,
    vlr_perc_min_beneficiario: regras.beneficiarios.percentualMinimo,
    desc_observacao: regras.observacoes.trim() || null,
    desc_excecao: excecoes.trim() || null,
    id_situacoes: [...regras.situacoesElegiveis],
    combinacoes: regras.combinacoes.map((combinacao) => ({
      qtd_idade_minima: combinacao.idadeMinima,
      qtd_tempo_vinculacao: combinacao.tempoMinimoVinculacao,
    })),
    id_comprovacoes: [...regras.comprovacoes],
    impedimentos: regras.impedimentos.map((impedimento) => ({
      id: impedimento.idApi,
      qtd_idade_minima: impedimento.idadeMinima,
      qtd_idade_maxima: impedimento.idadeMaxima,
      desc_msg_nao_elegivel: impedimento.mensagem.trim() || null,
      id_beneficios: [...impedimento.beneficios],
    })),
  };
}
